import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OnboardingVideo from "@/components/OnboardingVideo";
import api from "@/lib/api";
import { toast } from "sonner";
import { PlayCircle } from "lucide-react";

export default function Register({ role = "client" }) {
    const { register } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedCat = searchParams.get("cat") || "";
    const nextUrl = searchParams.get("next") || "";
    const [selectedCategory, setSelectedCategory] = useState(preselectedCat);
    const [form, setForm] = useState({
        email: "",
        password: "",
        full_name: "",
        phone: "",
        company_name: "",
        siret: "",
    });
    const [acceptedCgu, setAcceptedCgu] = useState(false);
    const [loading, setLoading] = useState(false);
    // Auto-show the animated onboarding "video" once per session for new candidates
    const [videoOpen, setVideoOpen] = useState(() => {
        if (role !== "candidate") return false;
        try {
            return !sessionStorage.getItem("renforteo_onboarding_seen");
        } catch {
            return true;
        }
    });
    const closeVideo = () => {
        setVideoOpen(false);
        try {
            sessionStorage.setItem("renforteo_onboarding_seen", "1");
        } catch {}
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) {
            toast.error("Mot de passe : 6 caractères minimum");
            return;
        }
        if (!acceptedCgu) {
            toast.error("Merci d'accepter les Conditions Générales d'Utilisation");
            return;
        }
        setLoading(true);
        try {
            const user = await register({ ...form, role, accepted_cgu: true });
            // If a category was pre-selected (via ?cat=xxx or filter chips), push it to the profile.
            if (role === "candidate" && selectedCategory) {
                try {
                    await api.put("/candidates/me", { primary_job: selectedCategory });
                } catch (_) { /* non-blocking */ }
            }
            toast.success("Compte créé");
            // Post-signup redirect: honor ?next= if it points to a same-origin path
            let target;
            if (role === "candidate") {
                target = "/candidat/onboarding";
            } else if (nextUrl && nextUrl.startsWith("/") && !nextUrl.startsWith("//")) {
                target = nextUrl;
            } else {
                target = "/client";
            }
            nav(target);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de l'inscription");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <div className="max-w-2xl mx-auto px-6 py-16">
                <div className="sd-card p-8 fade-up">
                    <div className="overline mb-2">
                        {role === "candidate"
                            ? "Espace candidat"
                            : "Espace client"}
                    </div>
                    <h1 className="font-display text-3xl mb-2">
                        {role === "candidate"
                            ? "Rejoignez les professionnels."
                            : "Créez votre compte entreprise."}
                    </h1>
                    <p className="text-slate-500 mb-6 text-sm">
                        {role === "candidate"
                            ? "Complétez votre profil pour être proposé aux missions correspondant à vos compétences."
                            : "Publiez des missions, suivez les candidatures et gérez vos factures depuis un seul espace."}
                    </p>
                    {role === "candidate" && (
                        <>
                            <button
                                type="button"
                                onClick={() => setVideoOpen(true)}
                                className="mb-4 w-full flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-[#0B192C] to-[#132845] text-white text-left hover:shadow-lg transition-shadow"
                                data-testid="register-onboarding-video-btn"
                            >
                                <div className="w-11 h-11 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center shrink-0">
                                    <PlayCircle size={26} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs uppercase tracking-widest text-[#F5A623] mb-0.5">
                                        Nouveau ici ?
                                    </div>
                                    <div className="font-semibold text-sm">
                                        Regarder la vidéo — comment fonctionne Renforteo ?
                                    </div>
                                    <div className="text-xs text-white/70 mt-0.5">
                                        6 scènes, 40 secondes. On vous explique tout.
                                    </div>
                                </div>
                            </button>
                            <div className="mb-6 bg-[#0B192C]/5 border-l-4 border-[#0B192C] rounded-lg p-4">
                                <div className="text-sm font-semibold text-[#0B192C] mb-1">
                                    Statut requis : auto-entrepreneur / indépendant
                                </div>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                    Renforteo travaille <b>exclusivement</b> avec des professionnels
                                    immatriculés. Un <b>numéro de SIRET</b> (14 chiffres) est
                                    obligatoire à l'inscription. Après inscription, vous devrez
                                    également téléverser votre <b>KBIS / Fiche INSEE</b>,
                                    votre <b>carte d'identité</b> et un <b>justificatif de domicile</b>
                                    de moins de 3 mois pour valider votre profil.
                                </p>
                            </div>
                            <div className="mb-6 bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4">
                                <div className="text-sm font-semibold text-emerald-900 mb-1">
                                    💚 Pas encore de SIRET ? On vous accompagne — gratuitement.
                                </div>
                                <p className="text-xs text-slate-700 leading-relaxed">
                                    La création d'une auto-entreprise est <b>100 % gratuite</b> et se
                                    fait en 10 minutes sur le site officiel du gouvernement. Notre
                                    équipe peut vous aider à chaque étape (choix du code APE, adresse
                                    de domiciliation, régime de TVA) — écrivez-nous à{" "}
                                    <a
                                        href="mailto:contact@renforteo.fr?subject=Aide%20cr%C3%A9ation%20SIRET%20auto-entreprise"
                                        className="text-[#0B192C] font-semibold underline hover:text-emerald-700"
                                        data-testid="register-siret-help-mailto"
                                    >
                                        contact@renforteo.fr
                                    </a>
                                    {" "}et nous vous guidons jusqu'à l'obtention de votre SIRET
                                    (délai officiel : 8 à 15 jours après validation INSEE).
                                </p>
                            </div>
                            <div className="mb-6" data-testid="candidate-category-picker">
                                <div className="text-sm font-semibold text-[#0B192C] mb-2">
                                    Quel métier vous intéresse ?
                                    <span className="text-xs font-normal text-slate-400 ml-2">
                                        (facultatif — vous pourrez en cocher plusieurs après)
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {[
                                        { k: "ash", label: "🏥 Santé / ASH", hot: true },
                                        { k: "livreur", label: "🚴 Livraison", hot: true },
                                        { k: "femme_de_menage", label: "🧹 Entretien", hot: true },
                                        { k: "serveur", label: "🍽️ Serveur / Serveuse" },
                                        { k: "plongeur", label: "🍽️ Plongeur" },
                                        { k: "commis", label: "👨‍🍳 Commis de cuisine" },
                                        { k: "hote", label: "🎫 Hôte / Hôtesse" },
                                        { k: "agent_securite", label: "🛡️ Sécurité" },
                                        { k: "", label: "✨ Autre / plusieurs" },
                                    ].map((c) => (
                                        <button
                                            key={c.k || "any"}
                                            type="button"
                                            onClick={() => setSelectedCategory(c.k)}
                                            className={`relative text-left rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-colors ${
                                                selectedCategory === c.k
                                                    ? "border-[#F5A623] bg-[#F5A623]/10 text-[#0B192C]"
                                                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                            }`}
                                            data-testid={`register-cat-${c.k || "any"}`}
                                        >
                                            {c.label}
                                            {c.hot && (
                                                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-tight">
                                                    Fort demande
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold mb-1">
                                {role === "candidate"
                                    ? "Nom complet"
                                    : "Nom du responsable"}
                            </label>
                            <input
                                required
                                value={form.full_name}
                                onChange={(e) => set("full_name", e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="register-fullname-input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                E-mail
                            </label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="register-email-input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Téléphone
                            </label>
                            <input
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="register-phone-input"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold mb-1">
                                Mot de passe
                            </label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="register-password-input"
                            />
                        </div>
                        {role === "candidate" && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold mb-1">
                                    Numéro de SIRET <span className="text-red-600">*</span>
                                    <span className="text-xs font-normal text-slate-400 ml-2">
                                        (14 chiffres — auto-entrepreneur, EI, EURL, SASU…)
                                    </span>
                                </label>
                                <input
                                    required={role === "candidate"}
                                    inputMode="numeric"
                                    pattern="\d{14}"
                                    minLength={14}
                                    maxLength={14}
                                    value={form.siret}
                                    onChange={(e) => set("siret", e.target.value.replace(/\D/g, "").slice(0, 14))}
                                    placeholder="Ex : 82345678900012"
                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white font-mono"
                                    data-testid="register-candidate-siret-input"
                                />
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Pas encore de SIRET ? La création d'une auto-entreprise est <b>100 % gratuite</b>{" "}
                                    en 10 minutes sur{" "}
                                    <a
                                        href="https://formalites.entreprises.gouv.fr/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#0B192C] font-semibold underline hover:text-[#F5A623]"
                                    >
                                        formalites.entreprises.gouv.fr
                                    </a>. Besoin d'aide ? Écrivez à{" "}
                                    <a
                                        href="mailto:contact@renforteo.fr?subject=Aide%20cr%C3%A9ation%20SIRET%20auto-entreprise"
                                        className="text-emerald-700 font-semibold underline"
                                    >
                                        contact@renforteo.fr
                                    </a>{" "}— on vous accompagne gratuitement.
                                </p>
                            </div>
                        )}
                        {role === "client" && (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold mb-1">
                                        Nom de l'entreprise
                                    </label>
                                    <input
                                        value={form.company_name}
                                        onChange={(e) =>
                                            set("company_name", e.target.value)
                                        }
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        data-testid="register-company-input"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1">
                                        SIRET
                                    </label>
                                    <input
                                        value={form.siret}
                                        onChange={(e) => set("siret", e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                        data-testid="register-siret-input"
                                    />
                                </div>
                            </>
                        )}
                        {role === "candidate" && (
                            <div className="md:col-span-2">
                            </div>
                        )}
                        <div className="md:col-span-2 mt-2">
                            <label className="flex items-start gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={acceptedCgu}
                                    onChange={(e) => setAcceptedCgu(e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-[#F5A623] cursor-pointer"
                                    data-testid="register-cgu-checkbox"
                                />
                                <span className="text-xs text-slate-600 leading-relaxed">
                                    J'ai lu et j'accepte les{" "}
                                    <a
                                        href="/cgu"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#0B192C] font-semibold underline hover:text-[#F5A623]"
                                        data-testid="register-cgu-link"
                                    >
                                        Conditions Générales d'Utilisation
                                    </a>
                                    {" "}de Renforteo, y compris la clause de non-contournement
                                    (article 7) et la politique de confidentialité (RGPD).
                                </span>
                            </label>
                        </div>
                        <div className="md:col-span-2 mt-2">
                            <button
                                disabled={loading || !acceptedCgu}
                                className="btn-gold w-full disabled:opacity-50"
                                data-testid="register-submit-btn"
                            >
                                {loading
                                    ? "Création..."
                                    : "Créer mon compte"}
                            </button>
                        </div>
                    </form>
                    <div className="text-sm text-slate-500 mt-6 text-center">
                        Vous avez déjà un compte ?{" "}
                        <Link
                            to={nextUrl ? `/connexion?next=${encodeURIComponent(nextUrl)}` : "/connexion"}
                            data-testid="register-login-link"
                            className="text-[#0B192C] font-semibold hover:text-[#F5A623]"
                        >
                            Se connecter
                        </Link>
                    </div>
                </div>
            </div>
            {videoOpen && <OnboardingVideo onClose={closeVideo} />}
            <Footer />
        </div>
    );
}
